<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    :fullscreen="$vuetify.display.xs"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="text-wrap">
        {{ $t(editing ? "waste.form.edit_title" : "waste.form.new_title") }}
      </v-card-title>
      <v-card-text>
        <v-alert type="warning" variant="tonal" density="compact" class="mb-3">
          {{ $t("waste.form.provisional") }}
        </v-alert>
        <v-alert
          v-if="alert"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ alert }}
        </v-alert>

        <v-text-field
          v-model="form.name"
          :label="$t('waste.form.name')"
          :error-messages="fieldError('name')"
          :maxlength="nameMax"
          variant="outlined"
          density="comfortable"
          class="mb-2"
        />

        <v-autocomplete
          v-model="form.wasteCodeId"
          :items="codeOptions"
          :label="$t('waste.form.code')"
          :error-messages="fieldError('wasteCodeId')"
          :disabled="codeLocked"
          :loading="codesLoading"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="comfortable"
          class="mb-1"
        />
        <div v-if="hint" class="text-caption text-medium-emphasis mb-1">
          <span v-if="hint.suggestedTasks.length">
            {{ $t("waste.form.suggested_for", { tasks: suggestedText }) }}
          </span>
          <span v-if="hint.mirror">
            ·
            {{ $t("waste.form.mirror_alternative", { code: hint.mirror.code }) }}
          </span>
        </div>
        <div
          v-if="hint && !hint.verified"
          class="text-caption text-amber-darken-3 mb-1"
        >
          {{ $t("waste.form.not_verified") }}
        </div>
        <div v-if="codeLocked" class="text-caption text-medium-emphasis mb-2">
          {{ $t("waste.form.code_locked") }}
        </div>

        <div class="d-flex flex-wrap ga-3 mt-2">
          <v-select
            v-model="form.type"
            :items="typeOptions"
            :label="$t('waste.form.type')"
            :error-messages="fieldError('type')"
            :disabled="codeLocked && form.type === 'area'"
            variant="outlined"
            density="comfortable"
            class="flex-grow-1"
            style="min-width: 160px"
          />
          <v-select
            v-model="form.unit"
            :items="unitOptions"
            :label="$t('waste.form.unit')"
            :error-messages="fieldError('unit')"
            :disabled="codeLocked"
            variant="outlined"
            density="comfortable"
            class="flex-grow-1"
            style="min-width: 120px"
          />
        </div>

        <v-text-field
          v-model="form.tare"
          :label="$t('waste.form.tare')"
          :error-messages="fieldError('tare')"
          inputmode="decimal"
          suffix="kg"
          variant="outlined"
          density="comfortable"
          class="mb-2"
        />
        <v-textarea
          v-model="form.note"
          :label="$t('waste.form.note')"
          :error-messages="fieldError('note')"
          :maxlength="noteMax"
          rows="2"
          auto-grow
          variant="outlined"
          density="comfortable"
        />
      </v-card-text>
      <v-card-actions class="flex-wrap ga-2">
        <v-spacer />
        <v-btn variant="text" :disabled="saving" @click="close">
          {{ $t("waste.form.cancel") }}
        </v-btn>
        <v-btn
          v-if="!editing"
          variant="outlined"
          color="primary"
          :loading="saving && printAfter"
          :disabled="saving"
          @click="save(true)"
        >
          {{ $t("waste.form.create_and_print") }}
        </v-btn>
        <v-btn
          color="red-darken-3"
          variant="flat"
          :loading="saving && !printAfter"
          :disabled="saving"
          @click="save(false)"
        >
          {{ $t(editing ? "waste.form.save" : "waste.form.create") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// New / edit container dialog (Stage 6 plan §D). Manager / Admin only: the page shows it only
// with canEdit / canCreate, and the API checks the role again. A commandId is kept per attempt,
// so a retry of the same save is answered as a replay.
import {
  createWasteContainer,
  getWasteCodes,
  newCommandId,
  updateWasteContainer,
} from "@/api/compliance";
import {
  CONTAINER_TYPES,
  NAME_MAX,
  NOTE_MAX,
  changedSet,
  codeHint,
  codeItems,
  createPayload,
  emptyForm,
  formFromContainer,
  serverError,
  unitsForType,
  validateForm,
} from "@/components/Waste/containerForm.mjs";

export default {
  name: "WasteContainerDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    // The container view to edit, or null for a new container.
    container: { type: Object, default: null },
    revision: { type: Number, default: 0 },
    codeLocked: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "saved"],
  data: () => ({
    form: emptyForm(),
    codes: [],
    codesLoading: false,
    errors: {},
    alert: "",
    saving: false,
    printAfter: false,
    pendingCommandId: "",
    nameMax: NAME_MAX,
    noteMax: NOTE_MAX,
  }),
  computed: {
    editing() {
      return Boolean(this.container);
    },
    codeOptions() {
      return codeItems(this.codes);
    },
    hint() {
      return codeHint(this.codes, this.form.wasteCodeId);
    },
    suggestedText() {
      return this.hint.suggestedTasks
        .map((task) => this.$t(`workshop.task.${task}`))
        .join(", ");
    },
    typeOptions() {
      return CONTAINER_TYPES.map((type) => ({
        value: type,
        title: this.$t(`waste.type.${type}`),
      }));
    },
    unitOptions() {
      return unitsForType(this.form.type).map((unit) => ({
        value: unit,
        title: this.$t(`waste.unit.${unit}`),
      }));
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) this.reset();
      },
    },
    "form.type"(type) {
      if (!unitsForType(type).includes(this.form.unit)) this.form.unit = "kg";
    },
  },
  methods: {
    reset() {
      this.form = this.container
        ? formFromContainer(this.container)
        : emptyForm();
      this.errors = {};
      this.alert = "";
      this.pendingCommandId = newCommandId();
      if (!this.codes.length) this.loadCodes();
    },
    async loadCodes() {
      this.codesLoading = true;
      try {
        const result = await getWasteCodes();
        this.codes = result.codes || [];
      } catch (err) {
        this.alert = this.errorText(err);
      } finally {
        this.codesLoading = false;
      }
    },
    fieldError(field) {
      return this.errors[field] ? [this.$t(this.errors[field])] : [];
    },
    close() {
      this.$emit("update:modelValue", false);
    },
    async save(printAfter) {
      this.errors = validateForm(this.form);
      this.alert = "";
      if (Object.keys(this.errors).length) return;
      this.printAfter = printAfter;
      this.saving = true;
      try {
        let detail;
        if (this.editing) {
          const set = changedSet(this.form, this.container, {
            codeLocked: this.codeLocked,
          });
          if (!Object.keys(set).length) {
            this.close();
            return;
          }
          detail = await updateWasteContainer(
            this.container.id,
            this.revision,
            this.pendingCommandId,
            set,
          );
        } else {
          detail = await createWasteContainer(
            this.pendingCommandId,
            createPayload(this.form),
          );
        }
        this.$emit("saved", detail, { print: printAfter });
        this.close();
      } catch (err) {
        const mapped = serverError(err);
        if (mapped && mapped.field) {
          this.errors = { [mapped.field]: mapped.key };
        } else {
          this.alert = mapped ? this.$t(mapped.key) : this.errorText(err);
        }
        // A refused save is a new attempt next time; after a network error the same commandId is
        // kept, so a retry that already reached the API is answered as a replay.
        if (err && err.response) this.pendingCommandId = newCommandId();
      } finally {
        this.saving = false;
      }
    },
    errorText(err) {
      return (
        (err &&
          err.response &&
          err.response.data &&
          err.response.data.errorMsg) ||
        (err && err.message) ||
        this.$t("waste.load_error")
      );
    },
  },
};
</script>
